<!--
// app/components/Shared/PaginationNav.vue
-->
<script setup lang="ts">
import PrevIcon from "~/app/components/Shared/icons/PrevIcon.vue";
import NextIcon from "~/app/components/Shared/icons/NextIcon.vue";
import { usePagination } from "~/app/composables/pages/pagination";
import { useRouteQuery } from "~/app/composables/routeQuery";
import { scrollToTop } from "~/src/Shared/utils";

const { resetPage, pageCount } = usePagination();
const { addQuery } = useRouteQuery();

type Props = {
  totalItemsAmount: number | undefined;
  itemsPerPage: number;
  scrollToTop?: boolean;
};

const props = defineProps<Props>();

const model = defineModel<number>({ required: true, default: 1 });

const updateQueryParams = (params: Record<string, any>) => {
  addQuery(params);
};

const goToPage = (page: number) => {
  scrollToTop();
  updateQueryParams({ page });
  model.value = page;
  if (page === 1) {
    resetPage();
  }
};

const visiblePages = computed((): number[] => {
  const startPage = Math.max(1, model.value - Math.floor(pageCount / 2));
  const endPage = Math.min(totalPages.value, startPage + pageCount - 1);
  return Array.from({ length: endPage - startPage + 1 }, (_, index) => startPage + index);
});

const totalPages = computed((): number => {
  return props.totalItemsAmount ? Math.ceil(props.totalItemsAmount / props.itemsPerPage) : 1;
});

// Watch for changes in totalPages
watch(
  totalPages,
  () => {
    if (totalPages.value >= 1 && (model.value <= 1 || model.value >= totalPages.value)) {
      resetPage();
    }
  },
  { immediate: true }
);
</script>

<template>
  <div v-if="totalPages && totalPages > 1" class="bigsm:col-span-2 order-last">
    <nav class="flex items-center justify-center mt-4 md:mt-8">
      <!--      Previous-->
      <nuxt-link
        v-if="model !== 1"
        @click="goToPage(model - 1)"
        :disabled="model === 1"
        class="mx-1 px-3 py-1 rounded-lg hover:text-primary"
      >
        <prev-icon :height="30" :width="30"></prev-icon>
      </nuxt-link>
      <!--      Page 1-->

      <nuxt-link
        v-if="Number(model) >= pageCount"
        @click="goToPage(1)"
        :disabled="model === 1"
        class="mx-1 px-3 py-1 rounded-lg hover:text-primary"
        >1</nuxt-link
      >

      <span v-if="model > pageCount"> ... </span>
      <!--      Visible Pages-->

      <template v-for="pageNumber in visiblePages" :key="pageNumber">
        <nuxt-link
          @click="goToPage(pageNumber)"
          :class="[
            'mx-1 px-3 py-1 rounded-lg',
            { 'bg-primary text-black': pageNumber === model },
            { 'hover:text-primary': pageNumber !== model },
          ]"
        >
          {{ pageNumber }}
        </nuxt-link>
      </template>

      <span v-if="model <= totalPages - pageCount"> ... </span>
      <!--   Last Page   -->

      <nuxt-link
        v-if="model < totalPages - pageCount + 2 && totalPages > pageCount"
        @click="goToPage(totalPages)"
        :disabled="model === totalPages"
        class="mx-1 px-3 py-1 rounded-lg hover:text-primary"
        >{{ totalPages }}</nuxt-link
      >
      <!--      Next Page-->
      <nuxt-link
        v-if="model < totalPages"
        @click="goToPage(model + 1)"
        :disabled="model === totalPages"
        class="mx-1 px-3 py-1 rounded-lg hover:text-primary"
        ><next-icon :height="30" :width="30"></next-icon
      ></nuxt-link>
    </nav>
  </div>
</template>

<style scoped></style>
